import { useEffect, useState, useRef } from "react";
import {
  Plus,
  Search,
  FileUp,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  DollarSign,
  UserCheck,
} from "lucide-react";
import api from "../../connector/api";
import { useNavigate, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

import AppLayout from "../components/common/AppLagout";
import NewPatronModal from "../components/NewPatronModal";
import BulkPatronModal from "../components/BulkPatronModal";

// Helper for generating avatar background colors matching design palette
const AVATAR_COLORS = [
  "bg-amber-500/20 text-amber-400 border-amber-500/30",
  "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  "bg-sky-500/20 text-sky-400 border-sky-500/30",
  "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "bg-rose-500/20 text-rose-400 border-rose-500/30",
  "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
];

export default function Patrons() {
  const navigate = useNavigate();

  // Patrons Data & Page Search state
  const [patrons, setPatrons] = useState([]);
  const [totalPatrons, setTotalPatrons] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const totalPages = Math.ceil(totalPatrons / itemsPerPage) || 1;

  // Header Global Book Search state
  const searchRef = useRef(null);
  const [headerSearchTerm, setHeaderSearchTerm] = useState("");
  const [headerSearchLoading, setHeaderSearchLoading] = useState(false);
  const [headerSearchResults, setHeaderSearchResults] = useState([]);
  const [headerShowDropdown, setHeaderShowDropdown] = useState(false);
  const [headerHighlightedIndex, setHeaderHighlightedIndex] = useState(-1);

  // Helper for normalizing cover image URLs
  const getCoverUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    const base = api.defaults.baseURL
      ? api.defaults.baseURL.replace(/\/api\/?$/, "")
      : "";
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${base}${normalizedPath}`;
  };

  // 1. Fetch Patrons (In-page filter)
  const fetchPatrons = async () => {
    try {
      const res = await api.get("/patrons/", {
        params: {
          page: currentPage,
          page_size: itemsPerPage,
          search: memberSearchTerm,
          status: statusFilter || undefined,
        },
      });
      setPatrons(res.data.results || []);
      setTotalPatrons(res.data.count || 0);
    } catch {
      toast.error("Failed to load patrons from administrative database");
    }
  };

  useEffect(() => {
    fetchPatrons();
  }, [currentPage, itemsPerPage, memberSearchTerm, statusFilter]);

  // 2. Debounced Header Book Search Effect
  useEffect(() => {
    if (!headerSearchTerm.trim()) {
      setHeaderSearchResults([]);
      setHeaderShowDropdown(false);
      return;
    }

    setHeaderSearchLoading(true);
    const handler = setTimeout(async () => {
      try {
        const res = await api.get("/cataloging/dashboard/search", {
          params: { q: headerSearchTerm },
        });
        const data = res.data || [];
        const normalizedResults = data.map((b) => ({
          ...b,
          cover_url: getCoverUrl(b.cover_image),
          is_available: b.status === "available",
        }));
        setHeaderSearchResults(normalizedResults);
        setHeaderShowDropdown(true);
      } catch (err) {
        console.error("Header search failed:", err);
        setHeaderSearchResults([]);
      } finally {
        setHeaderSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [headerSearchTerm]);

  // 3. Handle Selecting a Book from Header Dropdown
  const handleSelectBook = (selectedBook) => {
    const bookId = selectedBook?.id || selectedBook?._id;
    if (!bookId) return;

    setHeaderShowDropdown(false);
    setHeaderSearchTerm("");
    navigate(`/library/cataloging/book/detail/${bookId}`);
  };

  const handleAddPatron = async (patronData) => {
    const payload = { ...patronData };
    if (!payload.expiry_date) delete payload.expiry_date;
    if (!payload.email) delete payload.email;
    if (!payload.phone) delete payload.phone;

    try {
      await api.post("/patrons/", payload);
      toast.success("Patron record added successfully");
      setModalOpen(false);
      fetchPatrons();
    } catch (err) {
      toast.error("Failed to append directory entry");
      console.error("API Error:", err);
    }
  };

  const handleBulkUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/patrons/bulk_upload/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Successfully uploaded ${res.data.created} patrons`);
      fetchPatrons();
      return res.data;
    } catch (err) {
      toast.error("Bulk upload request failed");
      console.error("Bulk Upload Error:", err);
      throw err;
    }
  };

  const getInitials = (firstname = "", surname = "") => {
    const f = firstname ? firstname[0] : "";
    const s = surname ? surname[0] : "";
    return (f + s).toUpperCase() || "P";
  };

  return (
    <AppLayout
      headerProps={{
        title: "Patrons",
        searchTerm: headerSearchTerm,
        setSearchTerm: setHeaderSearchTerm,
        searchRef,
        searchLoading: headerSearchLoading,
        searchResults: headerSearchResults,
        showDropdown: headerShowDropdown,
        setShowDropdown: setHeaderShowDropdown,
        highlightedIndex: headerHighlightedIndex,
        setHighlightedIndex: setHeaderHighlightedIndex,
        handleSelectBook,
      }}
    >
      <Toaster position="top-right" />
      <div className="space-y-5 pb-8">
        {/* HEADER CONTROLS BAR */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 bg-[#1a1613] p-4 rounded-2xl border border-[#2e2720]">
          {/* SEARCH & FILTERS FOR MEMBERS */}
          <div className="flex flex-wrap items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-xs min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <input
                type="text"
                placeholder="Search members..."
                className="w-full pl-9 pr-4 h-9 bg-[#131110] border border-[#2e2720] rounded-xl text-xs font-semibold text-stone-100 placeholder-stone-500 focus:outline-none focus:border-amber-500/50 transition"
                value={memberSearchTerm}
                onChange={(e) => {
                  setMemberSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* STATUS TAB BUTTONS */}
            <div className="flex items-center gap-1 bg-[#131110] p-1 rounded-xl border border-[#2e2720]">
              {[
                { label: "All", value: "" },
                { label: "Active", value: "active" },
                { label: "Suspended", value: "suspended" },
                { label: "Expired", value: "expired" },
              ].map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => {
                    setStatusFilter(tab.value);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition ${
                    statusFilter === tab.value
                      ? "bg-amber-500 text-stone-950 shadow"
                      : "text-stone-400 hover:text-stone-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setBulkModalOpen(true)}
              className="flex items-center justify-center gap-2 px-3.5 h-9 bg-[#131110] border border-[#2e2720] text-stone-300 hover:text-amber-400 hover:border-amber-500/30 text-xs font-bold rounded-xl transition"
            >
              <FileUp className="w-3.5 h-3.5 text-stone-400" />
              Bulk Import
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="flex items-center justify-center gap-2 px-4 h-9 bg-amber-500 hover:bg-amber-400 text-stone-950 text-xs font-bold rounded-xl shadow transition"
            >
              <Plus className="w-4 h-4 stroke-[2.5]" />
              Add member
            </button>
          </div>
        </div>

        {/* PATRONS GRID DISPLAY */}
        {patrons.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {patrons.map((p, idx) => {
              const statusLower = (p.status || "").toLowerCase().trim();
              const colorClass = AVATAR_COLORS[idx % AVATAR_COLORS.length];

              return (
                <Link
                  key={p.id}
                  to={`/library/patrons/detail/${p.id}`}
                  className="group bg-[#1a1613] hover:bg-[#201c17] font-sans border border-[#2e2720] hover:border-amber-500/40 p-5 rounded-2xl flex flex-col items-center text-center transition-all shadow-md relative"
                >
                  {/* AVATAR INITIALS */}
                  <div
                    className={`w-14 h-14 rounded-full border-2 flex items-center justify-center font-bold text-base tracking-wider mb-3 shadow-inner ${colorClass}`}
                  >
                    {getInitials(p.firstname, p.surname)}
                  </div>

                  {/* MEMBER NAME & DETAILS */}
                  <h3 className="font-bold text-sm text-stone-100 group-hover:text-amber-400 transition line-clamp-1">
                    {p.firstname} {p.surname}
                  </h3>
                  <p className="text-[11px] text-stone-500 truncate max-w-full mb-3">
                    {p.email || p.cardnumber || "No Contact"}
                  </p>

                  {/* STATUS BADGE */}
                  <div className="mb-4">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase border ${
                        statusLower === "active"
                          ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                          : statusLower === "suspended"
                          ? "bg-rose-500/10 text-rose-400 border-rose-500/20"
                          : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                      }`}
                    >
                      {p.status || "Active"}
                    </span>
                  </div>

                  {/* BOTTOM STATS FOOTER */}
                  <div className="w-full border-t border-[#2e2720] pt-3 mt-auto flex items-center justify-around text-[11px] font-semibold text-stone-400">
                    <span className="flex items-center gap-1.5 hover:text-stone-200 transition-colors">
                      <BookOpen className="w-3.5 h-3.5 text-amber-500/80" />
                      <span className="text-stone-200 font-bold">
                        {p.books_out ?? 0}
                      </span>
                      <span>
                        {p.books_out === 1 ? "book out" : "books out"}
                      </span>
                    </span>

                    <span className="flex items-center gap-1 hover:text-stone-200 transition-colors">
                      <DollarSign className="w-3.5 h-3.5 text-emerald-500/80" />
                      <span className="text-stone-200 font-bold">
                        {Number(p.fines || 0).toLocaleString()}
                      </span>
                      <span>fines</span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          /* EMPTY STATE */
          <div className="bg-[#1a1613] border border-[#2e2720] rounded-2xl p-12 text-center">
            <UserCheck className="w-12 h-12 text-stone-600 mx-auto mb-3" />
            <h3 className="text-stone-300 font-bold text-sm">No Members Found</h3>
            <p className="text-stone-500 text-xs mt-1">
              No patron records matched your search query or selected status.
            </p>
          </div>
        )}

        {/* PAGINATION FOOTER */}
        <div className="bg-[#1a1613] border border-[#2e2720] px-5 py-3 rounded-2xl flex items-center justify-between text-xs font-semibold text-stone-400">
          <span>
            Showing <strong className="text-stone-200">{patrons.length}</strong> of{" "}
            <strong className="text-stone-200">{totalPatrons}</strong> members
          </span>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center bg-[#131110] border border-[#2e2720] rounded-xl text-stone-300 hover:text-amber-400 disabled:opacity-40 disabled:hover:text-stone-300 transition"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1 bg-[#131110] border border-[#2e2720] rounded-xl font-bold text-stone-200">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center bg-[#131110] border border-[#2e2720] rounded-xl text-stone-300 hover:text-amber-400 disabled:opacity-40 disabled:hover:text-stone-300 transition"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* MODAL WRAPPERS */}
      {modalOpen && (
        <NewPatronModal
          onClose={() => setModalOpen(false)}
          onSave={handleAddPatron}
          initialData={null}
        />
      )}

      {bulkModalOpen && (
        <BulkPatronModal
          onClose={() => setBulkModalOpen(false)}
          onUpload={handleBulkUpload}
          onUploadComplete={() => fetchPatrons()}
        />
      )}
    </AppLayout>
  );
}